import { useState, useMemo } from "react";
import { EstadoBadge } from "@/components/Badges";
import { eur, BACKEND } from "@/lib/api";
import { FilePdf, CheckCircle, WarningCircle, Table, Trash, Receipt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export function Registro({ facturas = [], onOpen, onDelete }) {
  const [filterTab, setFilterTab] = useState("all"); // "all" | "Facturas" | "Incidencias"
  const [showVatBreakdown, setShowVatBreakdown] = useState(false);

  const filtered = useMemo(() => {
    let list = facturas;
    if (filterTab === "Facturas") {
      list = facturas.filter((f) => f.sheet_tab === "Facturas" || f.estado === "OK" || f.estado === "ABONO");
    } else if (filterTab === "Incidencias") {
      list = facturas.filter((f) => f.sheet_tab === "Incidencias" || f.estado === "INCIDENCIA" || f.estado === "POSIBLE_DUPLICADO");
    }

    return [...list].sort((a, b) => {
      const ta = new Date(a.created_at || a.fecha || 0).getTime();
      const tb = new Date(b.created_at || b.fecha || 0).getTime();
      if (tb !== ta) return tb - ta;
      return String(b.fecha || "").localeCompare(String(a.fecha || ""));
    });
  }, [facturas, filterTab]);


  const countFacturas = facturas.filter((f) => f.sheet_tab === "Facturas" || f.estado === "OK" || f.estado === "ABONO").length;
  const countIncidencias = facturas.filter((f) => f.sheet_tab === "Incidencias" || f.estado === "INCIDENCIA" || f.estado === "POSIBLE_DUPLICADO").length;

  return (
    <div className="space-y-4 animate-fadein">
      {/* Barra de control y filtros */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex flex-wrap items-center gap-1.5">
          <Button
            variant={filterTab === "all" ? "default" : "ghost"}
            size="sm"
            onClick={() => setFilterTab("all")}
            className={`text-xs ${filterTab === "all" ? "bg-slate-900 text-white" : "text-slate-600"}`}
          >
            Todas ({facturas.length})
          </Button>
          <Button
            variant={filterTab === "Facturas" ? "default" : "ghost"}
            size="sm"
            onClick={() => setFilterTab("Facturas")}
            className={`gap-1.5 text-xs ${filterTab === "Facturas" ? "bg-emerald-600 text-white hover:bg-emerald-700" : "text-emerald-700 hover:bg-emerald-50"}`}
          >
            <CheckCircle size={14} weight="bold" /> Facturas ({countFacturas})
          </Button>
          <Button
            variant={filterTab === "Incidencias" ? "default" : "ghost"}
            size="sm"
            onClick={() => setFilterTab("Incidencias")}
            className={`gap-1.5 text-xs ${filterTab === "Incidencias" ? "bg-amber-600 text-white hover:bg-amber-700" : "text-amber-700 hover:bg-amber-50"}`}
          >
            <WarningCircle size={14} weight="bold" /> Incidencias ({countIncidencias})
          </Button>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowVatBreakdown(!showVatBreakdown)}
          className="gap-1.5 text-xs text-slate-600 border-slate-200 self-start sm:self-auto"
        >
          <Table size={14} />
          {showVatBreakdown ? "Ocultar IVA" : "Ver desglose IVA"}
        </Button>
      </div>

      {/* Tabla de registros con scroll táctil optimizado */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
          <table className="w-full text-sm min-w-[760px]" data-testid="registro-table">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Proveedor</th>
                <th className="px-4 py-3">CIF/NIF</th>
                <th className="px-4 py-3">Nº Factura</th>
                {showVatBreakdown ? (
                  <>
                    <th className="px-3 py-3 text-right text-emerald-700">Base 4%</th>
                    <th className="px-3 py-3 text-right text-blue-700">Base 10%</th>
                    <th className="px-3 py-3 text-right text-purple-700">Base 21%</th>
                    <th className="px-3 py-3 text-right">IVA Total</th>
                  </>
                ) : (
                  <>
                    <th className="px-4 py-3 text-right">Base Imponible</th>
                    <th className="px-4 py-3 text-right">IVA Total</th>
                  </>
                )}
                <th className="px-4 py-3 text-right font-bold text-slate-900">Total</th>
                <th className="px-4 py-3">Pestaña Sheet</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={showVatBreakdown ? 12 : 10} className="px-4 py-12 text-center text-slate-400">
                    <Receipt size={32} className="mx-auto mb-2 text-slate-300" />
                    <p className="font-medium text-slate-600">No hay facturas en esta vista.</p>
                    <p className="text-xs text-slate-400 mt-1">Las facturas procesadas de <code>facturacion@cocoand.com</code> aparecerán aquí.</p>
                  </td>
                </tr>
              )}
              {filtered.map((f) => (
                <tr
                  key={f.id}
                  data-testid={`registro-row-${f.id}`}
                  onClick={() => onOpen(f)}
                  className="hover:bg-slate-50/80 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{f.fecha || "—"}</td>
                  <td className="px-4 py-2.5 max-w-[190px]">
                    <div className="font-medium text-slate-800 truncate" title={f.proveedor_normalizado || f.proveedor}>
                      {f.proveedor_normalizado || f.proveedor}
                    </div>
                    {f.proveedor && f.proveedor !== f.proveedor_normalizado && (
                      <div className="text-[10px] text-slate-400 truncate">{f.proveedor}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{f.cif_nif || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-700">{f.num_factura || "—"}</td>

                  {showVatBreakdown ? (
                    <>
                      <td className="px-3 py-2.5 text-right font-mono text-xs text-emerald-700">
                        {f.base_4 > 0 ? eur(f.base_4) : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-xs text-blue-700">
                        {f.base_10 > 0 ? eur(f.base_10) : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-xs text-purple-700">
                        {f.base_21 > 0 ? eur(f.base_21) : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-500">
                        {eur(f.iva_total)}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-600">{eur(f.base_total)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-500">{eur(f.iva_total)}</td>
                    </>
                  )}

                  <td className="px-4 py-2.5 text-right font-mono text-xs font-bold text-slate-900">
                    {eur(f.total)}
                  </td>
                  <td className="px-4 py-2.5 text-xs">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${
                      f.sheet_tab === "Incidencias" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"
                    }`}>
                      {f.sheet_tab || (f.estado === "OK" ? "Facturas" : "Incidencias")}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <EstadoBadge estado={f.estado} />
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <a
                        href={`${BACKEND}/api/facturas/${f.id}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`pdf-link-${f.id}`}
                        className="text-slate-400 hover:text-rose-600 transition-colors inline-flex p-1.5 rounded hover:bg-slate-100"
                        title="Ver Documento"
                      >
                        <FilePdf size={18} />
                      </a>
                      {onDelete && (
                        <button
                          type="button"
                          onClick={() => onDelete(f.id)}
                          data-testid={`delete-btn-${f.id}`}
                          className="text-slate-400 hover:text-rose-600 transition-colors inline-flex p-1.5 rounded hover:bg-rose-50"
                          title="Eliminar factura"
                        >
                          <Trash size={17} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

